import { Component, inject, Signal } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, map, Observable, of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { ApexAnnotations, ApexDataLabels, ApexTooltip } from 'ng-apexcharts';

import { OrganizationState } from 'src/app/features/admin/organization/organization.state';
import { AuthState } from 'src/app/core/states/auth.state';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import { MenuItem } from 'src/app/shared/interfaces/menuitem.interface';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { convertNumber } from 'src/app/shared/all.helpers';
import { colorBlack } from 'src/app/shared/all.constants';

interface TabsItem {
    display: string;
    routerLink: any[];
    routerLinkActiveOptions?: any;
}

@UntilDestroy()
@Component({
    selector: 'app-cc-generic',
    templateUrl: './generic.component.html',
    styleUrls: ['./generic.component.scss'],
})
export class GenericComponent {
    public title = '';
    public subTitle = '';
    public tabItems$: Observable<TabsItem[]> | undefined = undefined;
    public _currentOrganization$ = inject(Store).select(OrganizationState.currentOrganization);
    public _currentYear$ = inject(Store).select(OrganizationState.currentYear);
    public _loggedUser$ = inject(Store).select(AuthState.loggedUser);

    public barDataLabelsPlotOptions = {};

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly sidenavServiceSuper: SidenavService,
        private readonly storeSuper: Store,
    ) {
        this.setTabs();

        this.setSideNavTopMenuItems();
    }

    ngOnDestroy(): void {
        // do not reset sidenav for now, but i have the feeling this will change again soon
        // this.sidenavServiceSuper.setTopMenuItems([]);
    }

    public async setTabs(): Promise<void> {
        this.activatedRoute.data.pipe(untilDestroyed(this)).subscribe((data) => {
            this.title = data['title'];
            this.subTitle = data['subTitle'];
            if (data['activeTab'] && data['tabs']) {
                this.tabItems$ = of(null).pipe(
                    map(() => {
                        return Object.keys(data['tabs']).map((key: string): TabsItem => {
                            const tab = data['tabs'][key];
                            return {
                                display: tab['title'],
                                routerLink: [`./${tab['path']}`],
                                routerLinkActiveOptions: { exact: false },
                            };
                        });
                    }),
                );
            }
        });
    }

    public async setSideNavTopMenuItems(): Promise<void> {
        combineLatest([this._currentOrganization$, this._currentYear$, this._loggedUser$])
            .pipe(untilDestroyed(this))
            .subscribe(([organization, year, loggedUser]) => {
                const prefixPath = `/main/${loggedUser?.isAdmin && organization?.id ? `${organization.id}/` : ''}cc/${year}`;
                const topMenuItems: MenuItem[] = [
                    {
                        label: CC_CONSTANTS.welcome.sidebarLabel,
                        icon: CC_CONSTANTS.welcome.icon,
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.welcome.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [],
                    },
                    {
                        label: CC_CONSTANTS.generalInfo.sidebarLabel,
                        icon: CC_CONSTANTS.generalInfo.icon,
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.generalInfo.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [],
                    },
                    {
                        label: 'Ouvrages',
                        icon: CC_CONSTANTS.sourceStations.icon,
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.sourceStations.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [
                            {
                                label: CC_CONSTANTS.sourceStations.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.sourceStations.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.mvNetworks.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.mvNetworks.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.lvNetworks.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.lvNetworks.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.transformerStations.sidebarLabel,
                                icon: '',
                                routerLink: [
                                    `${prefixPath}/${CC_CONSTANTS.transformerStations.path}`,
                                ],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                        ],
                    },
                    {
                        label: 'Continuité et Qualité',
                        icon: CC_CONSTANTS.continuityQuality.icon,
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.continuityQuality.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [
                            {
                                label: CC_CONSTANTS.continuityQuality.sidebarLabel,
                                icon: '',
                                routerLink: [
                                    `${prefixPath}/${CC_CONSTANTS.continuityQuality.path}`,
                                ],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.voltageQuality.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.voltageQuality.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                        ],
                    },
                    {
                        label: 'Investissements et maintenance',
                        icon: CC_CONSTANTS.investments.icon,
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.investments.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [
                            {
                                label: CC_CONSTANTS.investments.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.investments.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.maintenanceCost.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.maintenanceCost.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                        ],
                    },
                    {
                        label: 'Comptabilité',
                        icon: CC_CONSTANTS.kpis.icon,
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.kpis.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [
                            {
                                label: CC_CONSTANTS.kpis.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.kpis.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.grantorRights.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.grantorRights.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                        ],
                    },
                    {
                        label: CC_CONSTANTS.operatingAccount.sidebarLabel,
                        icon: CC_CONSTANTS.operatingAccount.icon,
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.operatingAccount.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [],
                    },
                    {
                        label: 'Clientèle Enedis',
                        icon: CC_CONSTANTS.energyUsers.icon,
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.energyUsers.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [
                            {
                                label: CC_CONSTANTS.energyUsers.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.energyUsers.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.energyProducers.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.energyProducers.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.connections.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.connections.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            // {
                            //     label: CC_CONSTANTS.enedisQuality.sidebarLabel,
                            //     icon: '',
                            //     routerLink: [`${prefixPath}/${CC_CONSTANTS.enedisQuality.path}`],
                            //     routerLinkActiveOptions: { exact: false },
                            //     hidden: false,
                            //     isActive: false,
                            //     subItems: [],
                            // },
                            {
                                label: CC_CONSTANTS.enedisClaims.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.enedisClaims.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                        ],
                    },
                    {
                        label: 'Clientèle EDF',
                        icon: CC_CONSTANTS.trvUsers.icon,
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.trvUsers.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [
                            {
                                label: CC_CONSTANTS.trvUsers.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.trvUsers.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.edfQuality.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.edfQuality.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.edfClaims.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.edfClaims.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                            {
                                label: CC_CONSTANTS.energyPoverty.sidebarLabel,
                                icon: '',
                                routerLink: [`${prefixPath}/${CC_CONSTANTS.energyPoverty.path}`],
                                routerLinkActiveOptions: { exact: false },
                                hidden: false,
                                isActive: false,
                                subItems: [],
                            },
                        ],
                    },
                    /*
                    {
                        label: CC_CONSTANTS.exportReport.sidebarLabel,
                        icon: 'icon-download-cloud-02',
                        routerLink: [`${prefixPath}/${CC_CONSTANTS.exportReport.path}`],
                        routerLinkActiveOptions: { exact: false },
                        hidden: false,
                        isActive: false,
                        subItems: [],
                    },
                    */
                ];
                this.sidenavServiceSuper.setTopMenuItems(topMenuItems);
            });
    }

    public checkKpiErrors(kpi: any, ...keys: any[]): boolean {
        const isKpiDefined = kpi.organizationId;
        const isErrorDefined = kpi.error;
        const areAllValuesDefined = keys.every((key) => ![null, undefined].includes(kpi[key]));
        const isErrorsDefined = isErrorDefined || !areAllValuesDefined;
        return !isKpiDefined || isErrorsDefined;
    }

    public checkChartDataForError(data: any[]): boolean {
        return data.length === 0 || !!data[0].error;
    }

    public checkChartValuesForError<T extends Record<string, any>>(
        data: T[],
        keys: (keyof T)[],
    ): boolean {
        //Return True if ALL datas are null or undefined
        return data.every((row) => keys.every((key) => [null, undefined].includes(row[key])));
    }

    public checkForError<T extends Record<string, any>>(data: T[], keys: (keyof T)[]): boolean {
        if (this.checkChartDataForError(data)) {
            return true;
        }
        return this.checkChartValuesForError(data, keys);
    }

    public setDataLabelsFormatter(
        index: number[],
        precision?: number,
    ): ApexDataLabels['formatter'] {
        return function (val: number, opts) {
            if (index.includes(opts.seriesIndex)) {
                return convertNumber(val, precision);
            }
            return '';
        };
    }

    public setDataLabelsFormatterBySerie(precisionByIndex: {
        [key: number]: number | null;
    }): ApexDataLabels['formatter'] {
        return function (val: number, opts) {
            const currentIndex = opts.seriesIndex;
            const precision = precisionByIndex[currentIndex];
            return convertNumber(val, precision);
        };
    }

    public setDataLabelsStyleColors(
        totalSerieName: string,
        totalSerieColor: string = colorBlack,
    ): ApexDataLabels['style'] {
        return {
            colors: [
                function (opts: any) {
                    const seriesName = opts.w.config.series[opts.seriesIndex].name;
                    return seriesName === totalSerieName
                        ? totalSerieColor
                        : opts.w.config.colors[opts.seriesIndex];
                },
            ],
        };
    }

    public setBarDataLabelsPlotOptions(
        position: string = 'center',
        offsetY: number = -10,
        precision: number | null = null,
    ) {
        const barDataLabelsPlotOptions = {
            position: position,
            hideOverflowingLabels: true,
            total: {
                enabled: true,
                formatter: function (val: string, chart: any) {
                    const collapsedSeriesIndices = chart.w.globals.collapsedSeriesIndices;
                    const isAnyActiveStackedBar = chart.config.series
                        .filter(
                            (serie: any, index: number) => !collapsedSeriesIndices.includes(index),
                        )
                        .some((serie: any) => serie.type === 'column');

                    if (isAnyActiveStackedBar) {
                        return convertNumber(Number(val), precision);
                    }
                    return '';
                },
                offsetY: offsetY,
            },
        };
        return barDataLabelsPlotOptions;
    }

    public getStackedTotals<T>(data: T[], keys: (keyof T)[]): number[] {
        return data.map((item) => keys.reduce((sum, key) => sum + (Number(item[key]) || 0), 0));
    }

    public setTotalAnnotation<T>(data: T[], keys: (keyof T)[], categories: string[]) {
        const stackedTotals = this.getStackedTotals(data, keys);

        return {
            points: stackedTotals.map((total, index) => ({
                x: categories[index],
                y: total,
                marker: {
                    size: 0,
                },
                label: {
                    text: convertNumber(total),
                    offsetY: total >= 0 ? -10 : 10,
                    style: {
                        fontWeight: 600,
                        background: 'rgba(55, 61, 63, 0.9)',
                        color: '#fff',
                    },
                },
            })),
        };
    }

    public setSimpleCustomTooltip(
        precision: number | null = null,
        seriesIndexWithTooltip: number[] | 'all' = 'all',
        unit: string = 'km',
    ): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            if (Array.isArray(seriesIndexWithTooltip)) {
                if (!seriesIndexWithTooltip.includes(seriesIndex)) {
                    return '';
                }
            }

            const dataSerie = w.globals.initialSeries[seriesIndex];
            const serieName = dataSerie.name;
            const serieColor = dataSerie.color;
            const dataPoint = dataSerie.data[dataPointIndex];
            let value = `${convertNumber(dataPoint.y, precision, false)}`;
            if (unit) {
                value += ` ${unit}`;
            }

            return `
                    <div class="apexcharts-tooltip-title">
                        ${dataPoint.x}
                    </div>
    
                    <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                        <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                        <div class="apexcharts-tooltip-text">
                            <div class="apexcharts-tooltip-y-group">
                                <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                                <span class="apexcharts-tooltip-text-y-value">${value}</span>
                            </div>
                             <div class="apexcharts-tooltip-goals-group">
                             </div>
                        </div>
                    </div>
                `;
        };
    }

    public setCustomTooltipData(
        precision: number | null = null,
        serie: any,
        dataPointIndex: number,
    ) {
        const name = serie.name;
        const color = serie.color;
        const value = serie.data[dataPointIndex]?.y ?? serie.data[dataPointIndex];
        const valueStringified = convertNumber(value, precision);
        return { name, color, valueStringified };
    }

    public setCustomTooltip(
        collapsedSeriesIndices: number[],
        title: string,
        dataTooltips: any[],
    ): string {
        let html = `
            <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                ${title}
            </div>
        `;

        dataTooltips.forEach((data, index) => {
            const showHideClass = collapsedSeriesIndices.includes(index) ? 'd-none' : 'd-flex';
            const color = data.color;
            const name = data.name;
            const value = data.value;
            const order = index + 1;
            const itemHtml = this.setCustomTooltipItem(showHideClass, color, name, value, order);
            html += itemHtml;
        });
        return html;
    }

    public setCustomTooltipItem(
        showHideClass: string,
        color: string,
        name: string,
        value: string,
        order: number,
    ): string {
        const colorSpan = color
            ? `<span class="apexcharts-tooltip-marker" style="background-color: ${color}"></span>`
            : '';

        const html = `
            <div class="apexcharts-tooltip-series-group apexcharts-active ${showHideClass}" style="order: ${order};">
                ${colorSpan}
                <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label"> ${name}: </span>
                        <span class="apexcharts-tooltip-text-y-value"> ${value} </span>
                    </div>
                        <div class="apexcharts-tooltip-goals-group">
                        </div>
                </div>
            </div>
        `;
        return html;
    }

    public setRangeBarHeight(stacheCount: number): string {
        return 15 * stacheCount + 20 + '%';
    }

    public getTotales(data: any[], fields: string[]): number[] {
        return data.map((d) => fields.reduce((sum, field) => sum + (d[field] ?? 0), 0));
    }

    public getLastValues<T>(data: T[], count: number): T[] {
        return data.slice(-count);
    }
}
